import {sortMessages} from "@web-libs/features/chat/domain/chat-message.functions";
import {findIndexById} from "@web-libs/util/arrays/array.functions";
import {sortByDateDesc} from "@web-libs/util/dates/date";
import {with_} from "@web-libs/util/with/with";
import {Chatroom} from "@web-libs/features/chat/domain/types/chatroom.type";
import {ChatMessage} from "@web-libs/features/chat/domain/types/chat-message.types";

export type ChatroomUpdate = { id: string } & Partial<Omit<Chatroom, 'id'>>;

/**
 *  PUBLIC FUNCTIONS
 */
export const sortChatroomsByLatestMessageDate =
    (chatroom: Chatroom[]) => sortByDateDesc(chatroom, _getLatestMessageOrCreationDate);

export const getLatestMessage = (chatroom: Chatroom): ChatMessage | undefined =>
    chatroom.messages.length === 0 ? undefined : chatroom.messages[chatroom.messages.length - 1];

/**
 * PROTECTED FUNCTIONS
 */
export const _findChatroomIndex = (chatrooms: Chatroom[], chatroomId: string) =>
    findIndexById(chatrooms, chatroomId, (chatroom) => chatroom.id, "Chatroom");
export const _findChatroom = (chatrooms: Chatroom[], chatroomId: string) =>
    chatrooms[_findChatroomIndex(chatrooms, chatroomId)];

export function _updateChatroom(chatroom: Chatroom, update: ChatroomUpdate) {
    const detailsUpdated = _updateChatroomDetails(chatroom, update);
    return update.messages ? _addMessages(detailsUpdated, update.messages) : detailsUpdated;
}

export const _resetUnreadMessages = (chatroom: Chatroom): Chatroom =>
    _updateChatroomDetails(chatroom, {unreadMessages: 0});

export const _incrementUnreadMessages = (chatroom: Chatroom): Chatroom =>
    _updateChatroomDetails(chatroom, {unreadMessages: chatroom.unreadMessages + 1});

export function _addMessages(chatroom: Chatroom, messagesToAdd: ChatMessage[]): Chatroom {
    const incomingMessages = _setDeliveryStatusOnIncomingMessages(chatroom, messagesToAdd);
    const combinedMessages = [..._removeChatMessages(chatroom, incomingMessages), ...incomingMessages];
    const sortedMessages = sortMessages(combinedMessages);
    return with_(chatroom, {messages: sortedMessages})
}

export const findMessageByInternalReferenceId = (chatroom: Chatroom, internalReferenceId?: string): ChatMessage | undefined =>
    chatroom.messages.find(message => internalReferenceId && message.internalReferenceId === internalReferenceId);

/**
 *  PRIVATE FUNCTIONS
 */
const _setDeliveryStatusOnIncomingMessages = (chatroom: Chatroom, incomingMessages: ChatMessage[]): ChatMessage[] =>
    incomingMessages.map(message => {
        const existingMessage = findMessageByInternalReferenceId(chatroom, message.internalReferenceId);
        if(existingMessage && message.ownMessage && existingMessage.ownMessage && message.deliveryStatus === "failed"){
            return with_(message, {deliveryStatus: "failed"});
        }
        return existingMessage && message.ownMessage && existingMessage.ownMessage ?
            with_(message, {deliveryStatus: message.deliveryStatus === "pending" ? "pending": "delivered"}) : message;
    });



function _updateChatroomDetails(chatroom: Chatroom, details: Partial<Chatroom>): Chatroom {
    return {
        ...chatroom,
        ...details,
        ...{messages: chatroom.messages}
    }
}

function isInternalReferenceIdAvailable(message: ChatMessage, messagesToRemove: ChatMessage[]) {
    if(message.internalReferenceId === undefined) {
        return false;
    }
    return messagesToRemove.some(_message => {
        if(_message.internalReferenceId === undefined) {
            return false;
        }
        return _message.internalReferenceId === message.internalReferenceId
    });
}

function _removeChatMessages(chatroom: Chatroom, messagesToRemove: ChatMessage[]): ChatMessage[] {
    const messageIds = messagesToRemove.map(message => message.messageId);
    return chatroom.messages.filter(message => {
        if (isInternalReferenceIdAvailable(message, messagesToRemove)) {
            return false;
        }
        if (message.messageId === undefined) {
            return true;
        }
        return !messageIds.includes(message.messageId)
    });
}

function _getLatestMessageOrCreationDate(chatroom: Chatroom): Date {
    return new Date(getLatestMessage(chatroom)?.sendDate || chatroom.initialSortDate || chatroom.creationDate || 0);
}







